/**
 * Utilidades de color para theming por organizador.
 *
 * Objetivo: dado un hex arbitrario de un organizador, generar una
 * paleta segura que:
 *   - Tenga contraste WCAG AA contra texto blanco o negro
 *   - No colisione con la semántica del sistema (verde éxito, ámbar
 *     aviso, rojo error)
 *   - Permita alpha suave para fondos hover y strong para hover
 *
 * Todo son funciones puras — testeables sin DOM.
 */

/** #RGB o #RRGGBB → {r,g,b} 0-255. Devuelve null si no parsea. */
export function parseHex(hex: string): { r: number; g: number; b: number } | null {
  if (!hex) return null;
  const s = hex.trim().replace(/^#/, '');
  if (s.length === 3) {
    return {
      r: parseInt(s[0] + s[0], 16),
      g: parseInt(s[1] + s[1], 16),
      b: parseInt(s[2] + s[2], 16),
    };
  }
  if (s.length === 6) {
    return {
      r: parseInt(s.slice(0, 2), 16),
      g: parseInt(s.slice(2, 4), 16),
      b: parseInt(s.slice(4, 6), 16),
    };
  }
  return null;
}

function toHex(n: number): string {
  const v = Math.max(0, Math.min(255, Math.round(n)));
  return v.toString(16).padStart(2, '0');
}

export function toHexString(r: number, g: number, b: number): string {
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Luminancia relativa WCAG (0..1) — para calcular contraste. */
export function relativeLuminance(hex: string): number {
  const c = parseHex(hex);
  if (!c) return 0;
  const chan = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(c.r) + 0.7152 * chan(c.g) + 0.0722 * chan(c.b);
}

/** Ratio de contraste WCAG entre dos colores (1..21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Devuelve #FFF o #000 — el que da mejor contraste sobre el fondo dado. */
export function readableTextOn(bgHex: string): string {
  return contrastRatio(bgHex, '#FFFFFF') >= contrastRatio(bgHex, '#000000') ? '#FFFFFF' : '#000000';
}

/** Cumple WCAG AA para texto grande sobre este fondo (ratio >= 3). */
export function passesAA(bgHex: string): boolean {
  return contrastRatio(bgHex, readableTextOn(bgHex)) >= 3;
}

/** Añade alpha en formato #RRGGBBAA (alpha 0..1). */
export function withAlpha(hex: string, alpha: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255);
  return `#${toHex(c.r)}${toHex(c.g)}${toHex(c.b)}${toHex(a)}`;
}

/** Oscurece un hex por un factor 0..1 (0.15 = 15% más oscuro). */
export function darken(hex: string, amount: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  const f = 1 - Math.max(0, Math.min(1, amount));
  return toHexString(c.r * f, c.g * f, c.b * f);
}

/** Aclara un hex mezclando con blanco por un factor 0..1. */
export function lighten(hex: string, amount: number): string {
  const c = parseHex(hex);
  if (!c) return hex;
  const f = Math.max(0, Math.min(1, amount));
  return toHexString(c.r + (255 - c.r) * f, c.g + (255 - c.g) * f, c.b + (255 - c.b) * f);
}

/**
 * Distancia perceptual sencilla (RGB euclidea, no CIEDE2000).
 * Suficiente para detectar "este color se parece demasiado a verde ok
 * o rojo error, no lo dejo como acento de marca".
 */
export function colorDistance(a: string, b: string): number {
  const ca = parseHex(a);
  const cb = parseHex(b);
  if (!ca || !cb) return Infinity;
  const dr = ca.r - cb.r;
  const dg = ca.g - cb.g;
  const db = ca.b - cb.b;
  return Math.sqrt(dr * dr + dg * dg + db * db);
}

/** Colores semánticos que un acento de marca NO debe imitar. */
export const SEMANTIC_COLORS = {
  success: '#22C55E',
  warning: '#FBBF24',
  error: '#EF4444',
} as const;

/**
 * Valida un acento propuesto:
 *   - Debe parsear como hex
 *   - Debe tener contraste AA contra al menos uno de {#FFF, #000}
 *   - No debe estar a <60 unidades RGB de un color semántico
 *
 * Devuelve {ok: true} o {ok: false, reason: 'motivo humano'}.
 */
export function validateBrandColor(hex: string): { ok: true } | { ok: false; reason: string } {
  const parsed = parseHex(hex);
  if (!parsed) return { ok: false, reason: 'No es un color hex válido (usa #RRGGBB).' };
  if (!passesAA(hex)) {
    return { ok: false, reason: 'Contraste insuficiente — no se lee ni sobre blanco ni sobre negro.' };
  }
  for (const [name, semantic] of Object.entries(SEMANTIC_COLORS)) {
    if (colorDistance(hex, semantic) < 60) {
      const human = name === 'success' ? 'verde de éxito' : name === 'warning' ? 'ámbar de aviso' : 'rojo de error';
      return { ok: false, reason: `Muy parecido al ${human} del sistema — los usuarios confundirían acciones.` };
    }
  }
  return { ok: true };
}

/**
 * Construye el objeto de tokens que consume EntityThemeProvider.
 * Todos son strings que se inyectan como CSS custom properties.
 */
export interface BrandTokens {
  brand: string;
  brandInk: string;      // texto legible sobre brand
  brandSoft: string;     // 9% alpha — fondos hover
  brandStrong: string;   // 15% más oscuro — hover de CTA
  brandBorder: string;   // 24% alpha — bordes activos
}

export function buildBrandTokens(accentHex: string | null | undefined): BrandTokens {
  const brand = (accentHex && parseHex(accentHex)) ? accentHex! : '#1E2A2E';
  return {
    brand,
    brandInk: readableTextOn(brand),
    brandSoft: withAlpha(brand, 0.09),
    brandStrong: darken(brand, 0.15),
    brandBorder: withAlpha(brand, 0.24),
  };
}
